import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Avatar,
  Menu,
  MenuItem,
  IconButton,
  useTheme,
  alpha,
} from '@mui/material';
import { 
  AccountCircle, 
  SportsHockey, 
  Home,
  EmojiEvents,
  Sports,
  AdminPanelSettings,
  Person,
  Logout
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiConfig } from '../config/api';

const Navbar = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleClose();
    navigate('/');
  };

  const handleLogin = () => {
    window.location.href = apiConfig.auth.google;
  };

  return (
    <AppBar 
      position="static" 
      sx={{ 
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        backdropFilter: 'blur(10px)',
      }}
    >
      <Toolbar sx={{ minHeight: 64 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mr: 3 }}>
          <SportsHockey sx={{ mr: 1.5, fontSize: 28 }} />
          <Typography 
            variant="h5" 
            component={Link} 
            to="/"
            sx={{ 
              fontWeight: 700,
              textDecoration: 'none',
              color: 'inherit',
              background: 'linear-gradient(45deg, #fff, #e3f2fd)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Liiga Ennustus
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
          <Button 
            color="inherit" 
            component={Link} 
            to="/"
            startIcon={<Home />}
            sx={{
              borderRadius: 2,
              px: 2,
              py: 1,
              '&:hover': {
                backgroundColor: alpha(theme.palette.common.white, 0.1),
                transform: 'translateY(-1px)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            Etusivu
          </Button>
          <Button 
            color="inherit" 
            component={Link} 
            to="/leaderboard"
            startIcon={<EmojiEvents />}
            sx={{
              borderRadius: 2,
              px: 2,
              py: 1,
              '&:hover': {
                backgroundColor: alpha(theme.palette.common.white, 0.1),
                transform: 'translateY(-1px)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            Tulostaulu
          </Button>
          
          {isAuthenticated ? (
            <>
              <Button 
                color="inherit" 
                component={Link} 
                to="/games"
                startIcon={<Sports />}
                sx={{
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.common.white, 0.1),
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                Ottelut
              </Button>
              {user?.role === 'admin' && (
                <Button 
                  color="inherit" 
                  component={Link} 
                  to="/admin"
                  startIcon={<AdminPanelSettings />}
                  sx={{
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.common.white, 0.1),
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  Hallinta
                </Button>
              )}
              <Box sx={{ ml: 1 }}>
                <IconButton
                  size="large"
                  onClick={handleMenu}
                  sx={{ 
                    p: 0.5,
                    border: `2px solid ${alpha(theme.palette.common.white, 0.2)}`,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.common.white, 0.1),
                      transform: 'scale(1.05)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  {user?.avatarUrl ? (
                    <Avatar 
                      src={user.avatarUrl} 
                      sx={{ 
                        width: 36, 
                        height: 36,
                        border: '2px solid rgba(255,255,255,0.3)',
                      }} 
                    />
                  ) : (
                    <AccountCircle sx={{ fontSize: 36, color: 'white' }} />
                  )}
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                  PaperProps={{
                    sx: {
                      mt: 1.5,
                      borderRadius: 2,
                      minWidth: 180,
                      boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                      '& .MuiMenuItem-root': {
                        py: 1,
                        px: 2,
                        borderRadius: 1,
                        margin: '4px 8px',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        },
                      },
                    },
                  }}
                >
                  <MenuItem onClick={() => { navigate('/profile'); handleClose(); }}>
                    <Person sx={{ mr: 1.5 }} />
                    Profiili
                  </MenuItem>
                  <MenuItem onClick={handleLogout}>
                    <Logout sx={{ mr: 1.5 }} />
                    Kirjaudu ulos
                  </MenuItem>
                </Menu>
              </Box>
            </>
          ) : (
            <Button 
              color="inherit" 
              onClick={handleLogin}
              variant="outlined"
              sx={{
                borderColor: alpha(theme.palette.common.white, 0.5),
                borderRadius: 2,
                px: 3,
                py: 1,
                fontWeight: 600,
                '&:hover': {
                  borderColor: theme.palette.common.white,
                  backgroundColor: alpha(theme.palette.common.white, 0.1),
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              Kirjaudu Googlella
            </Button>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
